import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  Row,
  Col,
  Button,
  Card,
  CardBody,
  Collapse,
  Form,
  FormFeedback,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  APIcreateFTPAccount,
  APIupdateFTPAccount,
} from '../../../api/Importers';
import { APIgetUsers } from '../../../api/User';

import { parseResponseErrors } from '../../../helpers';
import { userCan } from '../../../helpers/Auth';
import { getSetting } from '../../../helpers/Settings';

import CollectionSelectModal from '../../CollectionSelectModal';
import AccessModalSelect from '../../Access/ModalSelect';
import HelpIcon from '../../HelpIcon';
import ProcessingSpinner from '../../ProcessingSpinner';

import ErrorBoundary from '../../ErrorBoundary';

class FTPAccountEditModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      login: '',
      loginSuffix: getSetting('ftp_login_suffix') || getSetting('domain'),
      description: '',
      password: '',
      passwordConfirmation: '',
      collection: null,
      user: null,
      exifMapping: getSetting('exif_mapping_enabled') && getSetting('use_exif_mapping_by_default'),
      autoTagging: false,
      saving: false,
      errors: {},
      users: [],
      usersLoading: false,
      collectionSelectOpen: false,
      ftpDetailsOpen: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleSaveAccount = this.handleSaveAccount.bind(this);
    this.handleNewCollection = this.handleNewCollection.bind(this);
    this.handleRemoveSelectedCollection = this.handleRemoveSelectedCollection.bind(this);
    this.handleSelectUser = this.handleSelectUser.bind(this);
    this.openCollectionSelectModal = this.openCollectionSelectModal.bind(this);
    this.closeCollectionSelectModal = this.closeCollectionSelectModal.bind(this);
  }

  componentDidMount() {
    this.loadUsers();

    const { account } = this.props;

    if (account) {
      this.setState({
        login: account.login,
        loginSuffix: account.login_suffix,
        description: account.description,
        collection: account.collection,
        user: account.user,
        exifMapping: account.exif_mapping,
        autoTagging: account.auto_tagging,
      });
    }
  }

  loadUsers() {
    this.setState({ usersLoading: true });

    APIgetUsers({ all: true }).then((response) => {
      const { data } = response;
      if (data) {
        this.setState({ users: data });
      }
      this.setState({ usersLoading: false });
    });
  }

  validateForm() {
    const {
      login,
      password,
      passwordConfirmation,
      collection,
    } = this.state;
    const { intl, account } = this.props;
    const errors = {};

    if (!login) {
      errors.login = intl.formatMessage({ defaultMessage: 'This field is required' });
    }

    if (!account && !password && !passwordConfirmation) {
      errors.password = intl.formatMessage({ defaultMessage: 'This field is required' });
    }
    if (password || passwordConfirmation) {
      if (password !== passwordConfirmation) {
        errors.password = intl.formatMessage({ defaultMessage: 'Passwords do not match' });
      }
    }

    if (!collection) {
      errors.collection = intl.formatMessage({ defaultMessage: 'You have to select a Collection' });
    }

    this.setState({ errors });

    if (Object.keys(errors).length > 0) {
      return false;
    }

    return true;
  }

  handleChange(e) {
    const {
      name,
      value,
      checked,
      type,
    } = e.target;

    if (type === 'checkbox') {
      this.setState({ [name]: checked });
    } else {
      this.setState({ [name]: value });
    }
  }

  handleKeyPress(e) {
    e.persist();
    if (e.key === 'Enter') {
      e.preventDefault();
      this.handleSaveAccount();
    }
  }

  handleSaveAccount() {
    if (!this.validateForm()) {
      return;
    }

    const {
      login,
      loginSuffix,
      description,
      password,
      passwordConfirmation,
      collection,
      user,
      exifMapping,
      autoTagging,
    } = this.state;
    const {
      account,
      handleClose,
      notificationCallback,
    } = this.props;

    this.setState({ saving: true });

    const updatedAccount = {};
    updatedAccount.login = `${login}@${loginSuffix}`;
    updatedAccount.description = description;
    updatedAccount.password = password;
    updatedAccount.password_confirmation = passwordConfirmation;
    updatedAccount.collection_uuid = collection.uuid;
    updatedAccount.user_uuid = user ? user.uuid : null;
    updatedAccount.exif_mapping = exifMapping;
    updatedAccount.auto_tagging = autoTagging;

    if (account) {
      APIupdateFTPAccount(account.id, updatedAccount)
        .then((response) => {
          if (response.data) {
            notificationCallback(response.data);
            handleClose();
          }
        })
        .catch((error) => {
          this.setState({
            errors: parseResponseErrors(error.response),
            saving: false,
          });
        });
    } else {
      APIcreateFTPAccount(updatedAccount)
        .then((response) => {
          if (response.data) {
            notificationCallback(response.data);
            handleClose();
          }
        })
        .catch((error) => {
          this.setState({
            errors: parseResponseErrors(error.response),
            saving: false,
          });
        });
    }
  }

  handleNewCollection(collection) {
    const { errors } = this.state;

    if (errors.collection) {
      delete errors.collection;
    }
    this.setState({
      collection,
      errors,
    });
  }

  handleRemoveSelectedCollection() {
    this.setState({ collection: null });
  }

  handleSelectUser(userUuid) {
    const { users } = this.state;

    this.setState({ user: users.find(u => (u.uuid === userUuid)) || null });
  }

  openCollectionSelectModal() {
    this.setState({ collectionSelectOpen: true });
  }

  closeCollectionSelectModal() {
    this.setState({ collectionSelectOpen: false });
  }

  render() {
    const {
      login,
      loginSuffix,
      description,
      collection,
      user,
      exifMapping,
      autoTagging,
      errors,
      saving,
      users,
      usersLoading,
      collectionSelectOpen,
      ftpDetailsOpen,
    } = this.state;
    const {
      intl,
      account,
      handleClose,
    } = this.props;
    const ftpServer = getSetting('ftp_server');

    return (
      <ErrorBoundary>
        <ModalHeader toggle={handleClose} className="modal-header-center">
          {account ? (
            intl.formatMessage({ defaultMessage: 'Edit FTP account' })
          ) : (
            intl.formatMessage({ defaultMessage: 'New FTP account' })
          )}
        </ModalHeader>
        <ModalBody>
          <Form
            className="ftp-account-form"
            onKeyPress={this.handleKeyPress}
          >
            <Row form>
              <Col className="ftp-server-details">
                <a
                  href="#ftp-details"
                  onClick={(e) => {
                    e.preventDefault();
                    this.setState({ ftpDetailsOpen: !ftpDetailsOpen });
                  }}
                >
                  {ftpDetailsOpen ? (
                    <i className="fas fa-caret-down" />
                  ) : (
                    <i className="fas fa-caret-right" />
                  )}
                  {intl.formatMessage({ defaultMessage: 'How to configure your FTP client?' })}
                </a>
                <Collapse isOpen={ftpDetailsOpen}>
                  <Card>
                    <CardBody>
                      {intl.formatMessage({ defaultMessage: 'Host:' })} {ftpServer.host}<br />
                      {intl.formatMessage({ defaultMessage: 'Port:' })} {ftpServer.port}<br />
                      {intl.formatMessage({ defaultMessage: 'Passive mode:' })} {ftpServer.passive ? intl.formatMessage({ defaultMessage: 'yes' }) : intl.formatMessage({ defaultMessage: 'no' })}
                    </CardBody>
                  </Card>
                </Collapse>
              </Col>
            </Row>
            <Row form>
              <Col>
                <FormGroup>
                  <Label for="inpLogin">
                    {intl.formatMessage({ defaultMessage: 'Account login' })}
                  </Label>
                  <InputGroup>
                    <Input
                      type="text"
                      name="login"
                      id="inpLogin"
                      placeholder=""
                      defaultValue={login}
                      onChange={this.handleChange}
                      invalid={errors.login !== undefined}
                    />
                    <InputGroupAddon addonType="append">
                      <InputGroupText>@{loginSuffix}</InputGroupText>
                    </InputGroupAddon>
                    {errors.login !== undefined && (
                      <FormFeedback>{errors.login}</FormFeedback>
                    )}
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>

            <Row form>
              <Col>
                <FormGroup>
                  <Label for="inpPassword">
                    {intl.formatMessage({ defaultMessage: 'Password' })}
                  </Label>
                  <Input
                    type="password"
                    name="password"
                    id="inpPassword"
                    placeholder={intl.formatMessage({ defaultMessage: 'Type password' })}
                    onChange={this.handleChange}
                    invalid={errors.password !== undefined}
                  />
                  {errors.password !== undefined && (
                    <FormFeedback>{errors.password}</FormFeedback>
                  )}
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label for="inpPasswordConfirmation">
                    {intl.formatMessage({ defaultMessage: 'Retype Password' })}
                  </Label>
                  <Input
                    type="password"
                    name="passwordConfirmation"
                    id="inpPasswordConfirmation"
                    placeholder={intl.formatMessage({ defaultMessage: 'Type password' })}
                    onChange={this.handleChange}
                    invalid={errors.password !== undefined}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row form>
              <Col>
                <FormGroup className={classnames({ 'is-invalid': errors.role !== undefined })}>
                  <Label for="inpDescription">
                    {intl.formatMessage({ defaultMessage: 'Description' })}
                  </Label>
                  <Input
                    type="text"
                    name="description"
                    id="inpDescription"
                    placeholder={intl.formatMessage({ defaultMessage: 'Account description' })}
                    defaultValue={description}
                    onChange={this.handleChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="connect-collection" form>
              <Col>
                <FormGroup className={classnames({ 'is-invalid': errors.collection !== undefined })}>
                  <Label for="inpCollection">
                    {intl.formatMessage({ defaultMessage: 'Connect with Collection' })}
                  </Label>
                  {errors.collection !== undefined && (
                    <FormFeedback>{errors.collection}</FormFeedback>
                  )}
                  <Button
                    className="add-collection"
                    color="secondary"
                    onClick={this.openCollectionSelectModal}
                  >
                    {intl.formatMessage({ defaultMessage: 'Select or create Collection' })}
                  </Button>
                  {collection && (
                    <div className="tags-list">
                      <div className="tag">
                        <span className="tag-label">
                          {collection.name}
                        </span>
                        {/* eslint-disable jsx-a11y/click-events-have-key-events */}
                        <span
                          className="tag-remove"
                          title={intl.formatMessage({ defaultMessage: 'Remove' })}
                          role="button"
                          tabIndex={-1}
                          onClick={this.handleRemoveSelectedCollection}
                        >
                          <i className="far fa-times" />
                        </span>
                        {/* eslint-enable jsx-a11y/click-events-have-key-events */}
                      </div>
                    </div>
                  )}
                </FormGroup>
                <CollectionSelectModal
                  modalOpen={collectionSelectOpen}
                  enableAddNew={userCan('collection.create')}
                  handleClose={this.closeCollectionSelectModal}
                  handleCollectionSelect={this.handleNewCollection}
                  smartCollectionsRestrict={false}
                  showSmartSection={false}
                />
              </Col>
            </Row>

            <Row className="connect-user" form>
              <Col>
                <FormGroup>
                  <Label for="inpUser">
                    {intl.formatMessage({ defaultMessage: 'Assign user to FTP account' })}
                  </Label>
                  <AccessModalSelect
                    groups={[]}
                    users={users}
                    selectedUsers={user ? { [user.uuid]: 1 } : {}}
                    loading={usersLoading}
                    listType="users"
                    singleSelect={true}
                    handleAddItem={this.handleSelectUser}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="additional-options" form>
              <Col xs={12}>
                <Label className="additional-options-label">
                  {intl.formatMessage({ defaultMessage: 'Additional options' })}
                </Label>
              </Col>
              <Col>
                <FormGroup check inline>
                  <Label for="switchExifMapping" className="switch">
                    <div className="switch-control input-inner">
                      <Input
                        type="checkbox"
                        name="exifMapping"
                        id="switchExifMapping"
                        value="true"
                        checked={exifMapping && getSetting('exif_mapping_enabled')}
                        disabled={!getSetting('exif_mapping_enabled')}
                        onChange={this.handleChange}
                      />
                      <i />
                    </div>
                    <div className="switch-details">
                      <span>
                        {intl.formatMessage({ defaultMessage: 'EXIF mapping' })}
                      </span>
                    </div>
                  </Label>
                  {!getSetting('exif_mapping_enabled') && (
                    <HelpIcon>
                      {intl.formatMessage({ defaultMessage: 'You need to enable IPTC & EXIF mapping in your workspace settings first' })}
                    </HelpIcon>
                  )}
                </FormGroup>
              </Col>
              <Col>
                <FormGroup check inline>
                  <Label for="switchAutoTagging" className="switch">
                    <div className="switch-control input-inner">
                      <Input
                        type="checkbox"
                        name="autoTagging"
                        id="switchAutoTagging"
                        value="true"
                        checked={autoTagging}
                        onChange={this.handleChange}
                      />
                      <i />
                    </div>
                    <div className="switch-details">
                      <span>
                        {intl.formatMessage({ defaultMessage: 'Auto-generated tagging' })}
                      </span>
                    </div>
                  </Label>
                </FormGroup>
              </Col>
            </Row>

          </Form>
        </ModalBody>
        <ModalFooter>
          <div className="buttons-group">
            <Button
              color="secondary"
              disabled={saving}
              onClick={handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
            <Button
              color="primary"
              disabled={saving}
              onClick={this.handleSaveAccount}
            >
              <ProcessingSpinner show={saving} />
              {intl.formatMessage({ defaultMessage: 'Save' })}
            </Button>
          </div>
        </ModalFooter>
      </ErrorBoundary>
    );
  }
}

FTPAccountEditModal.propTypes = {
  intl: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  account: PropTypes.object,
  notificationCallback: PropTypes.func,
};

FTPAccountEditModal.defaultProps = {
  account: null,
  notificationCallback: () => { },
};

export default injectIntl(FTPAccountEditModal);
